/**
 * Feature categories data for the PurchasePuffin Features Page.
 *
 * 6 e-commerce feature categories with inline SVG icons and detailed
 * descriptions. Lives outside `pages/` — like payday-web's + planner-web's
 * `data/FeatureCategoriesData.jsx` — so the MA11Y-001 cross-app contract
 * treats it as a data module instead of demanding a paired axe test, and so
 * the Features page itself stays inside the 500-line budget (it was 602 code
 * lines, and this catalogue was the entire overage). Category `id`s are the
 * scroll-spy anchor targets and must stay in sync with the nav.
 *
 * Extracted VERBATIM from `pages/FeaturesPage.jsx` (GEN0-STRUCTURAL-ELIMINATION):
 * the old `max-lines` disable claimed splitting "would fragment the page's
 * cohesive narrative" — but a static data catalogue is not narrative, and the
 * page's components are unchanged.
 *
 * @module portals/marketing-website/data/FeatureCategoriesData
 */

/**
 * Feature categories with detailed features
 * @constant {Array<Object>}
 */
/**
 * The `product-management` category.
 *
 * @param {Function} t - the marketing-portal translator
 * @returns {Object}
 */
function buildProductManagement(t) {
  return {
    id: 'product-management',
    title: t('marketingFeatures.product-management.title'),
    navTitle: t('marketingFeatures.product-management.navTitle'),
    description: t('marketingFeatures.product-management.description'),
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
        <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
        <line x1="12" y1="22.08" x2="12" y2="12" />
      </svg>
    ),
    features: [
      {
        name: t('marketingFeatures.product-management.features.custom-product-builder.name'),
        description: t(
          'marketingFeatures.product-management.features.custom-product-builder.description',
        ),
        details: t('marketingFeatures.product-management.features.custom-product-builder.details'),
      },
      {
        name: t('marketingFeatures.product-management.features.variant-management.name'),
        description: t(
          'marketingFeatures.product-management.features.variant-management.description',
        ),
        details: t('marketingFeatures.product-management.features.variant-management.details'),
      },
      {
        name: t('marketingFeatures.product-management.features.personalization-engine.name'),
        description: t(
          'marketingFeatures.product-management.features.personalization-engine.description',
        ),
        details: t('marketingFeatures.product-management.features.personalization-engine.details'),
      },
      {
        name: t('marketingFeatures.product-management.features.digital-proof-generation.name'),
        description: t(
          'marketingFeatures.product-management.features.digital-proof-generation.description',
        ),
        details: t(
          'marketingFeatures.product-management.features.digital-proof-generation.details',
        ),
      },
      {
        name: t(
          'marketingFeatures.product-management.features.category-collection-management.name',
        ),
        description: t(
          'marketingFeatures.product-management.features.category-collection-management.description',
        ),
        details: t(
          'marketingFeatures.product-management.features.category-collection-management.details',
        ),
      },
      {
        name: t('marketingFeatures.product-management.features.bulk-import-export.name'),
        description: t(
          'marketingFeatures.product-management.features.bulk-import-export.description',
        ),
        details: t('marketingFeatures.product-management.features.bulk-import-export.details'),
      },
    ],
  };
}

/**
 * The `storefront` category.
 *
 * @param {Function} t - the marketing-portal translator
 * @returns {Object}
 */
function buildStorefront(t) {
  return {
    id: 'storefront',
    title: t('marketingFeatures.storefront.title'),
    navTitle: t('marketingFeatures.storefront.navTitle'),
    description: t('marketingFeatures.storefront.description'),
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <polyline points="9 22 9 12 15 12 15 22" />
      </svg>
    ),
    features: [
      {
        name: t('marketingFeatures.storefront.features.headless-architecture.name'),
        description: t('marketingFeatures.storefront.features.headless-architecture.description'),
        details: t('marketingFeatures.storefront.features.headless-architecture.details'),
      },
      {
        name: t('marketingFeatures.storefront.features.custom-theme-system.name'),
        description: t('marketingFeatures.storefront.features.custom-theme-system.description'),
        details: t('marketingFeatures.storefront.features.custom-theme-system.details'),
      },
      {
        name: t('marketingFeatures.storefront.features.responsive-by-default.name'),
        description: t('marketingFeatures.storefront.features.responsive-by-default.description'),
        details: t('marketingFeatures.storefront.features.responsive-by-default.details'),
      },
      {
        name: t('marketingFeatures.storefront.features.seo-ready-pages.name'),
        description: t('marketingFeatures.storefront.features.seo-ready-pages.description'),
        details: t('marketingFeatures.storefront.features.seo-ready-pages.details'),
      },
      {
        name: t('marketingFeatures.storefront.features.cms-page-builder.name'),
        description: t('marketingFeatures.storefront.features.cms-page-builder.description'),
        details: t('marketingFeatures.storefront.features.cms-page-builder.details'),
      },
      {
        name: t('marketingFeatures.storefront.features.multi-tenant-storefronts.name'),
        description: t(
          'marketingFeatures.storefront.features.multi-tenant-storefronts.description',
        ),
        details: t('marketingFeatures.storefront.features.multi-tenant-storefronts.details'),
      },
    ],
  };
}

/**
 * The `order-management` category.
 *
 * @param {Function} t - the marketing-portal translator
 * @returns {Object}
 */
function buildOrderManagement(t) {
  return {
    id: 'order-management',
    title: t('marketingFeatures.order-management.title'),
    navTitle: t('marketingFeatures.order-management.navTitle'),
    description: t('marketingFeatures.order-management.description'),
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
        <rect x="9" y="3" width="6" height="4" rx="1" />
        <path d="M9 14l2 2 4-4" />
      </svg>
    ),
    features: [
      {
        name: t('marketingFeatures.order-management.features.order-lifecycle-tracking.name'),
        description: t(
          'marketingFeatures.order-management.features.order-lifecycle-tracking.description',
        ),
        details: t('marketingFeatures.order-management.features.order-lifecycle-tracking.details'),
      },
      {
        name: t('marketingFeatures.order-management.features.production-queue.name'),
        description: t('marketingFeatures.order-management.features.production-queue.description'),
        details: t('marketingFeatures.order-management.features.production-queue.details'),
      },
      {
        name: t('marketingFeatures.order-management.features.fulfillment-workflows.name'),
        description: t(
          'marketingFeatures.order-management.features.fulfillment-workflows.description',
        ),
        details: t('marketingFeatures.order-management.features.fulfillment-workflows.details'),
      },
      {
        name: t('marketingFeatures.order-management.features.shipping-integration.name'),
        description: t(
          'marketingFeatures.order-management.features.shipping-integration.description',
        ),
        details: t('marketingFeatures.order-management.features.shipping-integration.details'),
      },
      {
        name: t('marketingFeatures.order-management.features.returns-exchanges.name'),
        description: t('marketingFeatures.order-management.features.returns-exchanges.description'),
        details: t('marketingFeatures.order-management.features.returns-exchanges.details'),
      },
      {
        name: t('marketingFeatures.order-management.features.order-notifications.name'),
        description: t(
          'marketingFeatures.order-management.features.order-notifications.description',
        ),
        details: t('marketingFeatures.order-management.features.order-notifications.details'),
      },
    ],
  };
}

/**
 * The `pricing-engine` category.
 *
 * @param {Function} t - the marketing-portal translator
 * @returns {Object}
 */
function buildPricingEngine(t) {
  return {
    id: 'pricing-engine',
    title: t('marketingFeatures.pricing-engine.title'),
    navTitle: t('marketingFeatures.pricing-engine.navTitle'),
    description: t('marketingFeatures.pricing-engine.description'),
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <line x1="12" y1="1" x2="12" y2="23" />
        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
      </svg>
    ),
    features: [
      {
        name: t('marketingFeatures.pricing-engine.features.volume-discounts.name'),
        description: t('marketingFeatures.pricing-engine.features.volume-discounts.description'),
        details: t('marketingFeatures.pricing-engine.features.volume-discounts.details'),
      },
      {
        name: t('marketingFeatures.pricing-engine.features.rush-fee-management.name'),
        description: t('marketingFeatures.pricing-engine.features.rush-fee-management.description'),
        details: t('marketingFeatures.pricing-engine.features.rush-fee-management.details'),
      },
      {
        name: t('marketingFeatures.pricing-engine.features.partner-pricing-tiers.name'),
        description: t(
          'marketingFeatures.pricing-engine.features.partner-pricing-tiers.description',
        ),
        details: t('marketingFeatures.pricing-engine.features.partner-pricing-tiers.details'),
      },
      {
        name: t('marketingFeatures.pricing-engine.features.promotional-rules.name'),
        description: t('marketingFeatures.pricing-engine.features.promotional-rules.description'),
        details: t('marketingFeatures.pricing-engine.features.promotional-rules.details'),
      },
      {
        name: t('marketingFeatures.pricing-engine.features.dynamic-cost-calculation.name'),
        description: t(
          'marketingFeatures.pricing-engine.features.dynamic-cost-calculation.description',
        ),
        details: t('marketingFeatures.pricing-engine.features.dynamic-cost-calculation.details'),
      },
      {
        name: t('marketingFeatures.pricing-engine.features.currency-tax-handling.name'),
        description: t(
          'marketingFeatures.pricing-engine.features.currency-tax-handling.description',
        ),
        details: t('marketingFeatures.pricing-engine.features.currency-tax-handling.details'),
      },
    ],
  };
}

/**
 * The `partner-portals` category.
 *
 * @param {Function} t - the marketing-portal translator
 * @returns {Object}
 */
function buildPartnerPortals(t) {
  return {
    id: 'partner-portals',
    title: t('marketingFeatures.partner-portals.title'),
    navTitle: t('marketingFeatures.partner-portals.navTitle'),
    description: t('marketingFeatures.partner-portals.description'),
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <path d="M9 22V12h6v10" />
      </svg>
    ),
    features: [
      {
        name: t('marketingFeatures.partner-portals.features.white-label-storefronts.name'),
        description: t(
          'marketingFeatures.partner-portals.features.white-label-storefronts.description',
        ),
        details: t('marketingFeatures.partner-portals.features.white-label-storefronts.details'),
      },
      {
        name: t('marketingFeatures.partner-portals.features.blind-drop-shipping.name'),
        description: t(
          'marketingFeatures.partner-portals.features.blind-drop-shipping.description',
        ),
        details: t('marketingFeatures.partner-portals.features.blind-drop-shipping.details'),
      },
      {
        name: t('marketingFeatures.partner-portals.features.partner-api-access.name'),
        description: t('marketingFeatures.partner-portals.features.partner-api-access.description'),
        details: t('marketingFeatures.partner-portals.features.partner-api-access.details'),
      },
      {
        name: t('marketingFeatures.partner-portals.features.wholesale-ordering.name'),
        description: t('marketingFeatures.partner-portals.features.wholesale-ordering.description'),
        details: t('marketingFeatures.partner-portals.features.wholesale-ordering.details'),
      },
      {
        name: t('marketingFeatures.partner-portals.features.partner-dashboard.name'),
        description: t('marketingFeatures.partner-portals.features.partner-dashboard.description'),
        details: t('marketingFeatures.partner-portals.features.partner-dashboard.details'),
      },
      {
        name: t('marketingFeatures.partner-portals.features.inventory-reservation.name'),
        description: t(
          'marketingFeatures.partner-portals.features.inventory-reservation.description',
        ),
        details: t('marketingFeatures.partner-portals.features.inventory-reservation.details'),
      },
    ],
  };
}

/**
 * The `customer-experience` category.
 *
 * @param {Function} t - the marketing-portal translator
 * @returns {Object}
 */
function buildCustomerExperience(t) {
  return {
    id: 'customer-experience',
    title: t('marketingFeatures.customer-experience.title'),
    navTitle: t('marketingFeatures.customer-experience.navTitle'),
    description: t('marketingFeatures.customer-experience.description'),
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </svg>
    ),
    features: [
      {
        name: t('marketingFeatures.customer-experience.features.customer-accounts.name'),
        description: t(
          'marketingFeatures.customer-experience.features.customer-accounts.description',
        ),
        details: t('marketingFeatures.customer-experience.features.customer-accounts.details'),
      },
      {
        name: t('marketingFeatures.customer-experience.features.order-history-reorder.name'),
        description: t(
          'marketingFeatures.customer-experience.features.order-history-reorder.description',
        ),
        details: t('marketingFeatures.customer-experience.features.order-history-reorder.details'),
      },
      {
        name: t('marketingFeatures.customer-experience.features.wishlist-favorites.name'),
        description: t(
          'marketingFeatures.customer-experience.features.wishlist-favorites.description',
        ),
        details: t('marketingFeatures.customer-experience.features.wishlist-favorites.details'),
      },
      {
        name: t('marketingFeatures.customer-experience.features.real-time-order-tracking.name'),
        description: t(
          'marketingFeatures.customer-experience.features.real-time-order-tracking.description',
        ),
        details: t(
          'marketingFeatures.customer-experience.features.real-time-order-tracking.details',
        ),
      },
      {
        name: t('marketingFeatures.customer-experience.features.review-ratings.name'),
        description: t('marketingFeatures.customer-experience.features.review-ratings.description'),
        details: t('marketingFeatures.customer-experience.features.review-ratings.details'),
      },
      {
        name: t('marketingFeatures.customer-experience.features.multi-channel-notifications.name'),
        description: t(
          'marketingFeatures.customer-experience.features.multi-channel-notifications.description',
        ),
        details: t(
          'marketingFeatures.customer-experience.features.multi-channel-notifications.details',
        ),
      },
    ],
  };
}

/**
 * Build the feature categories with every user-facing string RESOLVED.
 *
 * AAS-I18N-002 ruling 79 (2026-08-11). This module used to export a frozen array holding **126
 * English strings** while `FeaturesPage` translated only its own chrome — so the Spanish `/features`
 * page showed a Spanish hero above an English feature taxonomy.
 *
 * 🔴 A FACTORY, not a const: a module-scope `t(...)` captures whichever translator existed when the
 * bundle was first evaluated, so the page would keep serving the boot locale after a language switch.
 * Callers memoise on `t`. Decomposed one builder per category — the single function exceeded the
 * 250-line cap, and structural rules are never suppressed.
 *
 * @param {Function} t - the marketing-portal translator
 * @returns {Array<Object>} categories with resolved copy
 */
export function buildFeatureCategories(t) {
  return [
    buildProductManagement(t),
    buildStorefront(t),
    buildOrderManagement(t),
    buildPricingEngine(t),
    buildPartnerPortals(t),
    buildCustomerExperience(t),
  ];
}
